/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, { AxiosPromise, AxiosRequestConfig, ResponseType } from 'axios';
import moment from 'moment';
import { CONFIG } from '../config/config';
import { MenuInterface } from '../interfaces/menu';
import { CONSTANTS } from './constants';
import { menuAdmin, menuDAF, menuGTI } from './menus';

/**
 * send async request
 * @param method "post" | "get" | "put" | "patch" | "delete"
 * @param baseURL
 * @param url
 * @param authorization set header authorization
 * @param params
 * @param data
 * @param content_type
 * @returns
 */
interface RetryOptions {
  retries?: number;
  retryDelay?: number;
  retryCondition?: (error: unknown) => boolean;
}

const defaultRetryOptions: Required<RetryOptions> = {
  retries: 2,
  retryDelay: 1000,
  retryCondition: (error: unknown) => {
    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as { response?: { status?: number } };
      const status = axiosError.response?.status;
      // Retry on network errors, timeouts, and 5xx server errors
      return !status || status >= 500 || status === 408;
    }
    // Retry on network errors (no response)
    return true;
  },
};

const sleep = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

export const sendAsyncRequest = async <T = unknown>(
  method: 'post' | 'get' | 'put' | 'patch' | 'delete',
  url: string,
  authorization?: boolean,
  params?: Record<string, unknown>,
  data?: Record<string, unknown>,
  responseType?: ResponseType,
  retryOptions?: RetryOptions,
): Promise<{ data: T; status: number; statusText: string }> => {
  const options = { ...defaultRetryOptions, ...retryOptions };
  const config: AxiosRequestConfig = {
    baseURL: CONFIG.BASE_URL_API,
    timeout: 20000,
    url,
    method,
    headers: {
      'content-type': 'application/json',
      'X-Application-Type': 'frontend-bo',
    },
  };
  if (params) {
    config.params = params;
  }
  if (data) {
    config.data = data;
  }
  if (authorization) {
    config.headers.Authorization = 'Bearer ' + localStorage.getItem(CONSTANTS.TOKEN);
  }
  if (responseType) {
    config.responseType = responseType;
  }

  let lastError: unknown;
  for (let attempt = 0; attempt <= options.retries; attempt++) {
    try {
      const response = await axios(config);
      return {
        data: response.data,
        status: response.status,
        statusText: response.statusText,
      };
    } catch (error) {
      lastError = error;
      const shouldRetry = attempt < options.retries && options.retryCondition(error);
      
      if (shouldRetry) {
        const delay = options.retryDelay * Math.pow(2, attempt); // Exponential backoff
        await sleep(delay);
        continue;
      }
      throw error;
    }
  }
  throw lastError;
};

/**
 * Confirms if there is a connected user or not
 *
 * @returns {boolean}
 */
export const isLoggedIn = () => {
  return localStorage.getItem(CONSTANTS.TOKEN) ? true : false;
};

export const isRoleValid = () => {
  try {
    const role = getRole();
    return role && CONSTANTS.ROLES.includes(role);
  } catch (error) {
    return false;
  }
};

export const getRole = () => {
  try {
    const user = getUser();
    return user && user?.roles && user?.roles[0] ? user.roles[0].role : '';
  } catch (error) {
    return '';
  }
};

export const getUser = () => {
  try {
    const user = localStorage.getItem(CONSTANTS.USER);
    if (user == null) {
      return '';
    } else {
      const decryptedUser = decrypt(user);
      if (!decryptedUser) {
        return '';
      }
      return JSON.parse(decryptedUser);
    }
  } catch (error) {
    // If decryption fails, clear localStorage and return empty
    localStorage.removeItem(CONSTANTS.USER);
    localStorage.removeItem(CONSTANTS.TOKEN);
    return '';
  }
};

/**
 * used to encrypt users roles saved in local storage
 *
 * @returns {string}
 */
export const encrypt = (value: string): string => {
  const CryptoJS = require('crypto-js');
  const secretKey = process.env.DOMANYS_SECTRET_KEY;
  if (!secretKey) {
    throw new Error('DOMANYS_SECTRET_KEY environment variable is required for encryption');
  }
  const encryptedText = CryptoJS.AES.encrypt(value, secretKey).toString();
  return encryptedText;
};

/**
 * used to decrypt users roles saved in local storage
 *
 * @returns {string}
 */
export const decrypt = (value: string): string => {
  try {
    const CryptoJS = require('crypto-js');
    const secretKey = process.env.DOMANYS_SECTRET_KEY;
    if (!secretKey) {
      throw new Error('DOMANYS_SECTRET_KEY environment variable is required for decryption');
    }
    const bytes = CryptoJS.AES.decrypt(value, secretKey);
    const decryptedText = bytes.toString(CryptoJS.enc.Utf8);
    
    // Check if decryption was successful (empty string means decryption failed)
    if (!decryptedText || decryptedText.length === 0) {
      throw new Error('Decryption failed: Invalid data or wrong key');
    }
    
    return decryptedText;
  } catch (error) {
    // Clear corrupted data from localStorage
    localStorage.removeItem(CONSTANTS.USER);
    localStorage.removeItem(CONSTANTS.TOKEN);
    throw new Error('Malformed UTF-8 data: Unable to decrypt user data');
  }
};

/**
 * used to get the current date in the format MM/DD/YYYY
 *
 *
 * @returns {string}
 */
export const getCurrentDate = (): string => {
  const today = new Date().toLocaleDateString();
  return today;
};

/**
 * verify if the app mode is dev
 *
 *
 * @returns {boolean}
 */
export const isDevMode = (): boolean => {
  return CONFIG.APP_MODE === 'development';
};

/**
 * rturn the correct menu array depending on the connected user
 *
 *
 * @returns {MenuInterface[]}
 */
export const getMenuByRole = (): MenuInterface[] => {
  const role = getRole();
  let menu: MenuInterface[] = [];

  switch (role) {
    case CONSTANTS.ADMIN_ROLE:
      menu = menuAdmin;
      break;
    case CONSTANTS.GTI_ROLE:
      menu = menuGTI;
      break;
    case CONSTANTS.DAF_ROLE:
      menu = menuDAF;
      break;
    default:
      menu = menuAdmin;
      break;
  }
  return menu;
};

/**
 * get the title to be displayed for the list of users to each role
 *
 *
 * @returns {string}
 */
export const getUsersTitleByRole = (): string => {
  const role = getRole();
  let menu = '';

  switch (role) {
    case CONSTANTS.ADMIN_ROLE:
      menu = 'users';
      break;
    case CONSTANTS.GTI_ROLE:
      menu = 'my_collaborators';
      break;
    case CONSTANTS.DAF_ROLE:
      menu = 'collaborators';
      break;
    default:
      menu = 'users';
      break;
  }
  return menu;
};

/**
 * verify if the current use is an admin
 *
 *
 * @returns {boolean}
 */
export const isAdmin = (): boolean => {
  const role = getRole();
  return role === CONSTANTS.ADMIN_ROLE;
};

export const reformatDate = (date: string): string => {
  const newDate = date.substring(0, 10);
  return newDate.replace(/-/g, '/');
};

/**
 * Validate email format
 *
 * @returns {boolean}
 */
export const isValidEmail = (email: string) => {
  return /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
    email,
  );
};

/**
 * Validate login length min-10 max-30
 *
 * @returns {boolean}
 */
export const isValidLogin = (login: string) => {
  return /^.{2,30}$/.test(login);
};

/**
 * Return a valid date format
 *
 * @returns {boolean}
 */
export const formatDate = (date: string) => {
  const d = new Date(date);
  return (
    ('0' + d.getDate()).slice(-2) +
    '/' +
    ('0' + (d.getMonth() + 1)).slice(-2) +
    '/' +
    d.getFullYear() +
    ' à ' +
    ('0' + d.getHours()).slice(-2) +
    ':' +
    ('0' + d.getMinutes()).slice(-2)
  );
};

/**
 * Return a valid date format from service Delivery
 *
 * @returns {boolean}
 */
export const formatDateServiceDelivery = (date: string) => {
  const d = new Date(date);
  return ('0' + d.getDate()).slice(-2) + '/' + ('0' + (d.getMonth() + 1)).slice(-2) + '/' + d.getFullYear();
};

/**
 * Return a valid date format for startDate and endDate
 *
 * @returns {boolean}
 */
export const formatStartEndDate = (date: Date | string) => {
  const d = new Date(date);
  return d.getFullYear() + '-' + ('0' + (d.getMonth() + 1)).slice(-2) + '-' + ('0' + d.getDate()).slice(-2);
};

/**
 * Return error msg
 *
 * @returns {string}
 */
import { ApiError as ApiErrorType, ApiErrorCode } from '../types/api.types';
import { getErrorI18nKey, getHttpStatusI18nKey } from './errorMapping';

export interface AxiosApiError {
  message?: string;
  msg?: string;
  error?: string | { message?: string };
  response?: {
    data?: {
      error?: string | { message?: string; code?: string };
      msg?: string;
      code?: string;
    };
    status?: number;
    statusText?: string;
  };
}

/**
 * Extract i18n key from error, with proper code mapping
 */
export const errorMsg = (event: unknown): string => {
  const defaultError = 'error.unknow';
  
  if (typeof event === 'string') {
    return event || defaultError;
  }
  
  if (event && typeof event === 'object') {
    // Check if it's our custom ApiError with code
    if (event instanceof ApiErrorType || ('code' in event && 'field' in event)) {
      const apiError = event as ApiErrorType;
      
      // Try to get i18n key from error code
      if (apiError.code) {
        const i18nKey = getErrorI18nKey(apiError.code as ApiErrorCode);
        if (i18nKey) {
          return i18nKey;
        }
      }
      
      // Try to get i18n key from status code
      if (apiError.statusCode) {
        const statusI18nKey = getHttpStatusI18nKey(apiError.statusCode);
        if (statusI18nKey) {
          return statusI18nKey;
        }
      }
      
      // Fallback to message
      if (apiError.message) {
        return apiError.message;
      }
    }
    
    const error = event as AxiosApiError;
    
    // Check for error code in response data
    const responseCode = error.response?.data?.code || 
      (typeof error.response?.data?.error === 'object' ? error.response?.data?.error?.code : undefined);
    
    if (responseCode) {
      const i18nKey = getErrorI18nKey(responseCode as ApiErrorCode);
      if (i18nKey) {
        return i18nKey;
      }
    }
    
    // Check for HTTP status codes and provide specific messages
    if (error.response?.status) {
      const status = error.response.status;
      const statusI18nKey = getHttpStatusI18nKey(status);
      if (statusI18nKey) {
        return statusI18nKey;
      }
    }
    
    // Check for network/timeout errors
    if (error.message) {
      if (error.message.includes('timeout') || error.message.includes('TIMEOUT')) {
        return 'error.timeout';
      }
      if (error.message.includes('Network Error') || error.message.includes('network')) {
        return 'error.network';
      }
    }
    
    // Check for nested error messages
    if (error.response?.data?.error) {
      const backendError = error.response.data.error;
      if (typeof backendError === 'string') {
        return backendError;
      }
      if (typeof backendError === 'object' && backendError.message) {
        return backendError.message;
      }
    }
    
    if (error.response?.data?.msg) {
      return error.response.data.msg;
    }
    
    if (error.message) {
      return error.message;
    }
    
    if (error.msg) {
      return error.msg;
    }
    
    if (error.error) {
      if (typeof error.error === 'string') {
        return error.error;
      }
      if (typeof error.error === 'object' && error.error.message) {
        return error.error.message;
      }
    }
    
    return 'error.network';
  }

  return defaultError;
};

/**
 * Extract the field name from an API error
 */
export const getErrorField = (event: unknown): string | undefined => {
  if (event && typeof event === 'object') {
    if ('field' in event) {
      return (event as { field?: string }).field;
    }
  }
  return undefined;
};

/**
 * Return a valid dateTime format for the calendar
 *
 * @returns {boolean}
 */
export const formatCalendarDate = (date: string, time: string) => {
  const d = new Date(date);
  const dateTimeString = d.getFullYear() + '-' + ('0' + (d.getMonth() + 1)).slice(-2) + '-' + ('0' + d.getDate()).slice(-2) + ' ' + time;
  const dateTime = new Date(dateTimeString);
  return dateTime;
};

/**
 * convert from float to hours
 * @param number
 * @returns string
 */
export const convertNumToTime = (number: number) => {
  if (!number) {
    return '00:00';
  }
  // Check sign of given number
  let sign: string | number = number >= 0 ? 1 : -1;

  // Set positive value of number of sign negative
  number = number * sign;

  // Separate the int from the decimal part
  let hour: string | number = Math.floor(number);
  let decpart = number - hour;

  const min = 1 / 60;
  // Round to nearest minute
  decpart = min * Math.round(decpart / min);

  let minute = Math.floor(decpart * 60) + '';

  // Add padding if need
  if (minute.length < 2) {
    minute = '0' + minute;
  }

  hour = hour + '';

  if (hour.length < 2) {
    hour = '0' + hour;
  }

  // Add Sign in final result
  sign = sign == 1 ? '' : '-';

  // Concate hours and minutes
  const time = sign + hour + ':' + minute;

  return time;
};

/**
 * get list of available times
 * @param number
 * @returns Array<string>
 */
export const getHoursByDuration = (number: number) => {
  const list = [];
  for (let i = 7; i <= 17; i++) {
    list.push(`${convertNumToTime(i)}-${convertNumToTime(i + number)}`);
  }
  return list;
};

export const getWeekDateKeys = (currentDate: Date, showWeekends: boolean): string[] => {
  const startOfWeek = moment(currentDate).startOf('isoWeek'); // Monday
  const daysToDisplay = showWeekends ? 7 : 5;
  return Array.from({length: daysToDisplay}, (_, index) =>
    startOfWeek.clone().add(index, 'days').format('YYYY-MM-DD'),
  );
};

/**
 * Calculate daily totals of hours from tasks
 * @param tasks Array of tasks with startDate and endDate
 * @param showWeekends Whether weekends are displayed
 * @param currentDate The current week date
 * @returns Object with date as key and formatted hours as value
 */
export const calculateDailyTotals = (
  tasks: Array<{ startDate?: Date | string; endDate?: Date | string }>,
  showWeekends: boolean,
  currentDate: Date,
): Record<string, string> => {
  const dateKeys = getWeekDateKeys(currentDate, showWeekends);
  const startOfWeek = moment(currentDate).startOf('isoWeek');
  const dayTotals: number[] = Array(dateKeys.length).fill(0);

  // Calculate hours for each task
  if (tasks && tasks.length > 0) {
    tasks.forEach((task) => {
      if (task.startDate && task.endDate) {
        const startMoment = moment(task.startDate);
        const endMoment = moment(task.endDate);

        if (!startMoment.isValid() || !endMoment.isValid()) {
          return;
        }

        const dayIndex = startMoment.clone().startOf('day').diff(startOfWeek, 'days');

        if (dayIndex >= 0 && dayIndex < dayTotals.length) {
          const durationHours = endMoment.diff(startMoment, 'minutes') / 60;
          dayTotals[dayIndex] += durationHours;
        }
      }
    });
  }

  // Convert to formatted strings (HH:mm)
  const formattedTotals: Record<string, string> = {};
  dateKeys.forEach((dateKey, index) => {
    formattedTotals[dateKey] = convertNumToTime(dayTotals[index]);
  });

  return formattedTotals;
};
